/**
 * Filters bar of the clients list: reply status, funnel stage (KAN-616), our
 * Telegram account (KAN-618), client source (KAN-682), outcome (KAN-737),
 * "Отработанные" (KAN-734: marked "Отработано", outcome not set yet), manager
 * (KAN-744), lead attribute (KAN-788), reset. Stages and
 * accounts are loaded here; when loading fails the corresponding filter is hidden.
 * KAN-782: every select carries a caption above it (FilterField), hidden
 * together with the select.
 */
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CLIENT_SOURCE_OPTIONS } from '@/types/client';
import type { FunnelStageConfig } from '@/types/funnel';
import {
  ALL_OUTCOMES,
  ALL_SENDERS,
  ALL_SOURCES,
  ALL_STAGES,
  type OutcomeFilterValue,
  type ReplyFilterValue,
} from './useClientListFilters';

/** KAN-737: options of the "Исход" filter. */
const OUTCOME_OPTIONS: { value: OutcomeFilterValue; label: string }[] = [
  { value: ALL_OUTCOMES, label: 'Все исходы' },
  { value: 'open', label: 'Открытые' },
  { value: 'won', label: 'Успех' },
  { value: 'lost', label: 'Отказ' },
];
import { stageOptionValue, useFunnelStageOptions } from './useFunnelStageOptions';
import { useSenderAccountOptions } from './useSenderAccountOptions';
import { ManagerFilter } from './ManagerFilter';
import { FILTER_TRIGGER_CLASS, FilterField } from './FilterField';
import { CrmAttributeFilter } from '@/components/crm/CrmAttributeFilter';
import { useLeadAttributes } from '@/hooks/useLeadAttributes';
import type { CrmAttributeFilter as AttributeFilterValue } from '@/lib/crmAttributeFilter';

interface ClientsFiltersProps {
  reply: ReplyFilterValue;
  stage: string;
  sender: string;
  source: string;
  worked: boolean;
  outcome: OutcomeFilterValue;
  manager: string;
  /** KAN-788: lead attribute filter; null = not set. */
  attributeFilter: AttributeFilterValue | null;
  isActive: boolean;
  onReplyChange: (value: ReplyFilterValue) => void;
  onStageChange: (value: string) => void;
  onSenderChange: (value: string) => void;
  onSourceChange: (value: string) => void;
  onWorkedChange: (value: boolean) => void;
  onOutcomeChange: (value: OutcomeFilterValue) => void;
  onManagerChange: (value: string) => void;
  onAttributeFilterChange: (value: AttributeFilterValue | null) => void;
  onReset: () => void;
}

/**
 * KAN-682: the funnel name prefixes the stage label only when the stages
 * belong to several funnels (estimator cabinets); with one funnel the prefix
 * is noise.
 */
function stageOptionLabel(stage: FunnelStageConfig, funnelCount: number): string {
  return funnelCount > 1 && stage.funnel_name
    ? `${stage.funnel_name}: ${stage.label}`
    : stage.label;
}

function countFunnels(stages: FunnelStageConfig[]): number {
  return new Set(stages.map((s) => s.funnel_codename ?? '')).size;
}

export function ClientsFilters({
  reply,
  stage,
  sender,
  source,
  worked,
  outcome,
  manager,
  attributeFilter,
  isActive,
  onReplyChange,
  onStageChange,
  onSenderChange,
  onSourceChange,
  onWorkedChange,
  onOutcomeChange,
  onManagerChange,
  onAttributeFilterChange,
  onReset,
}: ClientsFiltersProps): JSX.Element {
  const stages = useFunnelStageOptions();
  const senderOptions = useSenderAccountOptions();
  const leadAttributes = useLeadAttributes();
  const funnelCount = stages ? countFunnels(stages) : 0;

  return (
    <>
      <FilterField label="Ответ">
        <Select value={reply} onValueChange={(v) => onReplyChange(v as ReplyFilterValue)}>
          <SelectTrigger
            data-testid="reply-filter"
            aria-label="Ответ клиента"
            className={FILTER_TRIGGER_CLASS}
          >
            <SelectValue placeholder="Ответ клиента" className="whitespace-nowrap" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            <SelectItem value="replied">Ответил</SelectItem>
            <SelectItem value="not_replied">Не ответил</SelectItem>
          </SelectContent>
        </Select>
      </FilterField>

      {stages && (
        <FilterField label="Стадия">
          <Select value={stage} onValueChange={onStageChange}>
            <SelectTrigger
              data-testid="stage-filter"
              aria-label="Стадия воронки"
              className={FILTER_TRIGGER_CLASS}
            >
              <SelectValue placeholder="Стадия воронки" className="whitespace-nowrap" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STAGES}>Все стадии</SelectItem>
              {stages.map((s) => (
                <SelectItem key={s.id} value={stageOptionValue(s)}>
                  {stageOptionLabel(s, funnelCount)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      )}

      <FilterField label="Исход">
        <Select value={outcome} onValueChange={(v) => onOutcomeChange(v as OutcomeFilterValue)}>
          <SelectTrigger
            data-testid="outcome-filter"
            aria-label="Исход"
            className={FILTER_TRIGGER_CLASS}
          >
            <SelectValue placeholder="Исход" className="whitespace-nowrap" />
          </SelectTrigger>
          <SelectContent>
            {OUTCOME_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="Источник">
        <Select value={source} onValueChange={onSourceChange}>
          <SelectTrigger
            data-testid="source-filter"
            aria-label="Источник"
            className={FILTER_TRIGGER_CLASS}
          >
            <SelectValue placeholder="Источник" className="whitespace-nowrap" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SOURCES}>Все источники</SelectItem>
            {CLIENT_SOURCE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      {senderOptions && senderOptions.length > 0 && (
        <FilterField label="Наш аккаунт">
          <Select value={sender} onValueChange={onSenderChange}>
            <SelectTrigger
              data-testid="sender-filter"
              aria-label="Наш аккаунт"
              className={FILTER_TRIGGER_CLASS}
            >
              <SelectValue placeholder="Наш аккаунт" className="whitespace-nowrap" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SENDERS}>Все аккаунты</SelectItem>
              {senderOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      )}

      <ManagerFilter value={manager} onChange={onManagerChange} label="Менеджер" />

      <CrmAttributeFilter
        attributes={leadAttributes}
        filter={attributeFilter}
        onChange={onAttributeFilterChange}
      />

      <label className="flex h-10 items-center gap-2 whitespace-nowrap text-sm text-gray-700">
        <Checkbox
          data-testid="worked-filter"
          checked={worked}
          onCheckedChange={onWorkedChange}
        />
        В работе
      </label>

      {isActive && (
        <Button
          type="button"
          variant="ghost"
          data-testid="reset-filters-button"
          onClick={onReset}
        >
          Сбросить фильтры
        </Button>
      )}
    </>
  );
}
